import { useEffect, useState } from 'react';
import ItemsSlider from 'epn-ui/umd/dist/components/ItemsSlider';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';

import { IGetOffersDataFormatted } from '@api/types';

import styles from './styles.module.scss';
import OfferCard from './OfferCard';
import getMobileScroll from './utils';

export interface IOffersSliderProps {
    title: string;
    titleClassName?: string;
    totalOffers: number | unknown;
    isLoadingOffers: boolean;
    onNextOffers: () => void;
    offers: IGetOffersDataFormatted[];
    loadMore: boolean;
}

const OffersSlider: React.FC<IOffersSliderProps> = ({
    title,
    titleClassName,
    totalOffers,
    isLoadingOffers,
    onNextOffers,
    offers,
    loadMore,
}) => {
    const screens = useBreakpoint();
    const [hideLeftBtn, setHideLeftBtn] = useState(false);
    const [hideRightBtn, setHideRightBtn] = useState(false);

    const onScrollHandler = (isNext: boolean): void => {
        const container = document.querySelector('.epn-slider__body');
        const item = document.querySelector('.epn-slider__item');
        const gap = screens.md ? 24 : 16;

        if (!container || !item) return;

        const itemWidthWithGap = item.clientWidth + gap;
        const remainingScroll =
            container.scrollWidth - (container.scrollLeft + container.clientWidth);

        const scrollValue = screens.xs ? getMobileScroll() : itemWidthWithGap;

        if (!isNext) {
            setTimeout(() => {
                container.scrollBy({
                    left: -scrollValue,
                    behavior: 'smooth',
                });
            }, 0);
            return;
        }

        const scrollElement = () => {
            setTimeout(() => {
                container.scrollBy({
                    left: scrollValue,
                    behavior: 'smooth',
                });
            }, 0);
        };

        if (loadMore && remainingScroll < itemWidthWithGap) {
            onNextOffers();
            scrollElement();
        } else {
            scrollElement();
        }
    };

    useEffect(() => {
        const container = document.querySelector('.epn-slider__body');
        const minScroll = 20;

        if (!container) return null;

        const handleScroll = () => {
            const remainingScroll =
                container.scrollWidth - (container.scrollLeft + container.clientWidth);

            const endOfContainer = remainingScroll <= minScroll;
            setHideRightBtn(
                loadMore ? offers.length === totalOffers && endOfContainer : endOfContainer,
            );
            setHideLeftBtn(container.scrollLeft < minScroll);
        };

        container.addEventListener('scroll', handleScroll);

        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [offers, isLoadingOffers, loadMore, totalOffers]);

    return (
        <div className={styles.offers}>
            <div className={styles.slider}>
                <ItemsSlider
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    title={
                        <p className={clsx(styles.subtitle, titleClassName)}>
                            {title}
                            <span className={styles.count}>{totalOffers}</span>
                        </p>
                    }
                >
                    {offers.map(offer => (
                        <OfferCard offer={offer} key={offer.id} />
                    ))}
                    {isLoadingOffers &&
                        [...new Array(4)].map((_, index) => (
                            <Skeleton
                                key={index}
                                loading={isLoadingOffers}
                                shape="square"
                                title={{
                                    className: styles.skeleton,
                                }}
                            />
                        ))}
                </ItemsSlider>
                <div
                    className={clsx(
                        styles.arrowContainer,
                        styles.arrowLeft,
                        hideLeftBtn ? styles.hidden : '',
                    )}
                >
                    <button
                        type="button"
                        onClick={() => onScrollHandler(false)}
                        className={styles.arrowBtn}
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
                <div
                    className={clsx(
                        styles.arrowContainer,
                        styles.arrowRight,
                        hideRightBtn ? styles.hidden : '',
                    )}
                >
                    <button
                        type="button"
                        onClick={() => onScrollHandler(true)}
                        className={styles.arrowBtn}
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OffersSlider;
